import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Shield, AlertTriangle, Check, Trash2, UserX, Crown, DollarSign } from "lucide-react";

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [flagged, setFlagged] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [revenue, setRevenue] = useState(null);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) navigate("/");
    }, [loading, user, navigate]);

    const load = async () => {
        try {
            const [s, f, subs, rev] = await Promise.all([
                api.get("/admin/summary"),
                api.get("/admin/flagged-posts"),
                api.get("/admin/subscribers"),
                api.get("/admin/revenue"),
            ]);
            setSummary(s.data);
            setFlagged(f.data);
            setSubscribers(subs.data);
            setRevenue(rev.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { if (user?.role === "admin") load(); }, [user]);

    const resolve = async (postId, action) => {
        const labels = { dismiss: "dismiss the reports", remove: "remove this post", ban: "ban the user (removes all their posts)" };
        if (!window.confirm(`Are you sure you want to ${labels[action]}?`)) return;
        setBusy(postId);
        try {
            await api.post(`/admin/posts/${postId}/resolve`, { action });
            toast.success(`Action ${action} applied`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    if (!user || user.role !== "admin") return null;

    return (
        <main className="max-w-4xl mx-auto px-4 py-8">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-lg brut-static bg-[#FF5A5F] flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h1 className="display text-3xl sm:text-4xl font-black">Moderation queue</h1>
                    <p className="text-sm text-[#4A4A4A] font-medium">Admins only. Handle flagged posts and community reports.</p>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-summary">
                    <Stat label="Open reports" value={summary.open_reports} bg="bg-[#FFB8D1]" />
                    <Stat label="Flagged posts" value={summary.flagged_posts} bg="bg-[#FF5A5F] text-white" />
                    <Stat label="Users" value={summary.total_users} />
                    <Stat label="Posts" value={summary.total_posts} />
                </div>
            )}

            {revenue && (
                <section className="mb-8" data-testid="admin-revenue">
                    <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="w-5 h-5" />
                        <h2 className="display text-xl font-black">Revenue ({revenue.count} paid)</h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <Stat label="Total collected" value={`$${revenue.total_usd.toFixed(2)}`} bg="bg-[#FFE55C]" />
                        <Stat label="Subscriptions" value={`$${(revenue.by_kind_usd.subscription || 0).toFixed(2)}`} />
                        <Stat label="Boosts" value={`$${(revenue.by_kind_usd.boost || 0).toFixed(2)}`} />
                        <Stat label="Tips" value={`$${(revenue.by_kind_usd.tip || 0).toFixed(2)}`} />
                    </div>
                    {revenue.transactions.length > 0 && (
                        <div className="brut-static bg-white rounded-xl p-4" data-testid="admin-tx-list">
                            <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#4A4A4A]">Recent transactions</div>
                            <ul className="divide-y divide-[#0A0A0A]/10 text-sm">
                                {revenue.transactions.slice(0, 25).map((t) => (
                                    <li key={t.session_id} className="py-2 flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-bold truncate">
                                                {t.kind || "payment"}
                                                {t.lookup_key ? <span className="text-[#4A4A4A]"> · {t.lookup_key}</span> : null}
                                                {t.username ? <span className="text-[#4A4A4A]"> · @{t.username}</span> : null}
                                            </div>
                                            <div className="text-[11px] text-[#4A4A4A]">{t.updated_at ? new Date(t.updated_at).toLocaleString() : ""}</div>
                                        </div>
                                        <div className="font-black shrink-0">${t.amount_usd.toFixed(2)}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            )}

            <section className="mb-8" data-testid="admin-subscribers">
                <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5" />
                    <h2 className="display text-xl font-black">Paying members ({subscribers.length})</h2>
                </div>
                {subscribers.length === 0 ? (
                    <div className="border-2 border-dashed border-[#0A0A0A] rounded-xl p-6 text-center bg-white/60 text-sm font-medium text-[#4A4A4A]">
                        No paying members yet. Subscribers show up here as soon as their first payment clears.
                    </div>
                ) : (
                    <div className="brut-static bg-white rounded-xl p-4">
                        <ul className="divide-y divide-[#0A0A0A]/10 text-sm">
                            {subscribers.map((s) => (
                                <li key={s.id} className="py-2 flex items-center justify-between gap-2" data-testid={`sub-${s.username}`}>
                                    <div className="min-w-0">
                                        <Link to={`/u/${s.username}`} className="font-bold hover:underline">@{s.username}</Link>
                                        <div className="text-[11px] text-[#4A4A4A] truncate">{s.email}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <span className={`inline-block px-2 py-0.5 rounded-full border-2 border-[#0A0A0A] text-[10px] font-black uppercase tracking-wider ${s.tier === "elite" ? "bg-[#FFE55C]" : "bg-[#FFB8D1]"}`}>
                                            {s.tier === "elite" ? "Sole Elite" : "Pod Member"}
                                        </span>
                                        <div className="text-[11px] text-[#4A4A4A] mt-0.5">
                                            {s.lookup_key?.includes("yearly") ? "yearly" : "monthly"}
                                            {s.current_period_end ? ` · renews ${new Date(s.current_period_end).toLocaleDateString()}` : ""}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5" />
                <h2 className="display text-xl font-black">Flagged ({flagged.length})</h2>
            </div>

            {flagged.length === 0 ? (
                <div className="border-2 border-dashed border-[#0A0A0A] rounded-xl p-8 text-center bg-white/60" data-testid="admin-empty">
                    <div className="font-black">All clear — no flagged posts.</div>
                    <div className="text-sm text-[#4A4A4A] mt-1 font-medium">A post is auto-flagged after 3 community reports.</div>
                </div>
            ) : (
                <ul className="space-y-4" data-testid="admin-flagged-list">
                    {flagged.map((p) => (
                        <li key={p.id} className="brut-static bg-white rounded-xl p-4" data-testid={`admin-flagged-${p.id}`}>
                            <div className="flex gap-3 mb-3">
                                <Link to={`/p/${p.id}`} className="shrink-0">
                                    <img src={fileUrl(p.image_path)} alt="" className="w-24 h-24 rounded-lg object-cover border-2 border-[#0A0A0A]" />
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <Link to={`/u/${p.username}`} className="font-bold hover:underline">@{p.username}</Link>
                                    <div className="text-xs text-[#4A4A4A]">{new Date(p.created_at).toLocaleString()}</div>
                                    {p.caption && <div className="text-sm mt-1 font-medium">{p.caption}</div>}
                                    <div className="text-xs text-[#4A4A4A] mt-1">
                                        Avg {p.avg_rating.toFixed(1)} · {p.rating_count} ratings · {p.reports.length} report{p.reports.length === 1 ? "" : "s"}
                                    </div>
                                </div>
                            </div>

                            {p.reports.length > 0 && (
                                <div className="brut-inset bg-[#FFFDF7] rounded-lg p-3 mb-3">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-[#4A4A4A] mb-1">Reports</div>
                                    <ul className="space-y-1 text-xs font-medium">
                                        {p.reports.map((r) => (
                                            <li key={`${r.by}-${r.at}`}><b>@{r.by}:</b> {r.reason}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => resolve(p.id, "dismiss")}
                                    disabled={busy === p.id}
                                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg brut bg-white font-bold text-sm"
                                    data-testid={`dismiss-${p.id}`}
                                >
                                    <Check className="w-3.5 h-3.5" /> Dismiss
                                </button>
                                <button
                                    onClick={() => resolve(p.id, "remove")}
                                    disabled={busy === p.id}
                                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg brut bg-[#FFB8D1] font-bold text-sm"
                                    data-testid={`remove-${p.id}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Remove post
                                </button>
                                <button
                                    onClick={() => resolve(p.id, "ban")}
                                    disabled={busy === p.id}
                                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg brut bg-[#FF5A5F] text-white font-bold text-sm"
                                    data-testid={`ban-${p.id}`}
                                >
                                    <UserX className="w-3.5 h-3.5" /> Ban user
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </main>
    );
}

function Stat({ label, value, bg = "bg-white" }) {
    return (
        <div className={`brut-static rounded-xl p-4 ${bg}`}>
            <div className="text-xs font-black uppercase tracking-wider opacity-70">{label}</div>
            <div className="display text-3xl font-black mt-1">{value}</div>
        </div>
    );
}
